import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, IconButton, styled } from '@mui/material';

// Styled Tooltip component
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: theme.palette.mode === 'light' ? '#1a202c' : '#f7fafc',
    color: theme.palette.mode === 'light' ? '#f7fafc' : '#1a202c',
    fontSize: '0.75rem',
    borderRadius: '8px',
    padding: '6px 12px',
    boxShadow: theme.shadows[2],
  },
  '& .MuiTooltip-arrow': {
    color: theme.palette.mode === 'light' ? '#1a202c' : '#f7fafc',
  },
}));

// TooltipIconButton component
const TooltipIconButton = ({
  title,
  children,
  placement = 'top',
  disabled = false,
  color = 'default',
  size = 'medium',
  ...props
}) => {
  // Use a wrapper for disabled buttons to allow tooltips on disabled elements
  const WrapperComponent = ({ children }) =>
    disabled ? (
      <span style={{ display: 'inline-block' }}>{children}</span>
    ) : (
      children
    );

  return (
    <WrapperComponent>
      <StyledTooltip
        title={title}
        placement={placement}
        arrow
        disableInteractive={disabled}
      >
        <span>
          <IconButton color={color} size={size} disabled={disabled} {...props}>
            {children}
          </IconButton>
        </span>
      </StyledTooltip>
    </WrapperComponent>
  );
};

TooltipIconButton.propTypes = {
  /**
   * The tooltip text.
   */
  title: PropTypes.node.isRequired,
  /**
   * The icon to display.
   */
  children: PropTypes.node.isRequired,
  /**
   * The placement of the tooltip.
   */
  placement: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end',
  ]),
  /**
   * If true, the button will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * The color of the button.
   */
  color: PropTypes.oneOf([
    'inherit',
    'default',
    'primary',
    'secondary',
    'success',
    'error',
    'info',
    'warning',
  ]),
  /**
   * The size of the button.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default TooltipIconButton;
